import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { API_BASE_URL, API_ENDPOINTS } from '@/config/api'
import type {
    LoginRequest,
    LoginResponse,
    StoreAnalyzeRequest,
    StoreAnalyzeResponse,
    CommentsDetailsRequest,
    CommentsDetailsResponse,
    GenerateReplyRequest,
    GenerateReplyResponse,
    StoreSettingsRequest,
    StoreSettingsResponse,
    CategoryKeywordRequest,
    CategoryKeywordResponse,
    StoreFlagRequest,
    StoreFlagResponse,
    QueryTypeRequest,
    QueryTypeResponse,
    ReplyCommentsRequest,
    ReplyCommentsResponse,
    StoreInfo,
} from '@/types/api'

class ApiService {
    private api: AxiosInstance
    private baseURL: string

    constructor() {
        this.baseURL = API_BASE_URL

        this.api = axios.create({
            baseURL: this.baseURL,
            timeout: 20000,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // 請求攔截器
        this.api.interceptors.request.use(
            (config) => {
                // 可以在這裡添加認證 token
                const token = localStorage.getItem('argameusertoken')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )

        // 響應攔截器
        this.api.interceptors.response.use(
            (response: AxiosResponse) => {
                return response
            },
            (error) => {
                console.error('API Error:', error)

                // 檢查是否為 400 狀態碼（token 過期）
                if (error.response && error.response.status === 400) {
                    console.warn('收到 400 狀態碼，判定為 token 過期')

                    // 檢查請求 URL，排除登入 API 本身（登入失敗不應跳轉）
                    const requestUrl = error.config?.url || ''
                    const requestBaseURL = error.config?.baseURL || this.baseURL
                    const fullUrl = requestUrl.startsWith('http') ? requestUrl : `${requestBaseURL}${requestUrl}`
                    const isLoginRequest = fullUrl.includes(API_ENDPOINTS.LOGIN) || requestUrl.includes(API_ENDPOINTS.LOGIN)

                    if (!isLoginRequest) {
                        // 清除本地存儲的認證相關資料
                        localStorage.removeItem('argameusertoken')
                        localStorage.removeItem('store_code')
                        localStorage.removeItem('login_message')
                        localStorage.removeItem('login_status')
                        localStorage.removeItem('isCompleteData')
                        localStorage.removeItem('login_timestamp')
                        localStorage.removeItem('login_data')

                        // 清除店家資訊
                        this.clearStoreInfo()

                        console.log('已清除所有登入資料，跳轉到登入頁面')

                        // 跳轉到登入頁面（使用 window.location 確保完全重新載入）
                        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
                            window.location.href = '/login'
                        }
                    }
                }

                return Promise.reject(error)
            }
        )
    }

    // 登入
    async login(credentials: LoginRequest): Promise<LoginResponse> {
        try {
            const response = await this.api.post<LoginResponse>(API_ENDPOINTS.LOGIN, credentials)

            // 儲存完整的登入 response 資料到 localStorage
            if (response.data.status === 'success') {
                // 儲存認證相關資料
                localStorage.setItem('argameusertoken', response.data.argameusertoken)
                localStorage.setItem('store_code', response.data.store_code)

                // 儲存其他登入資料
                localStorage.setItem('login_message', response.data.message || '')
                localStorage.setItem('login_status', response.data.status)
                localStorage.setItem('isCompleteData', response.data.isCompleteData)
                localStorage.setItem('login_timestamp', response.data.timestamp)

                // 儲存完整的登入資料作為 JSON（備用）
                const loginData = {
                    message: response.data.message,
                    status: response.data.status,
                    isCompleteData: response.data.isCompleteData,
                    store_code: response.data.store_code,
                    argameusertoken: response.data.argameusertoken,
                    timestamp: response.data.timestamp
                }
                localStorage.setItem('login_data', JSON.stringify(loginData))

                console.log('登入成功，已儲存資料到本地端:', loginData)
            }

            return response.data
        } catch (error) {
            console.error('Login error:', error)
            throw error
        }
    }

    // 取得店家分析
    // 注意：雖然 API 文件標示為 GET，但實際上後端需要 POST 請求並在 body 中傳遞參數
    async getStoreAnalyze(request: StoreAnalyzeRequest): Promise<StoreAnalyzeResponse> {
        try {
            const response = await this.api.post<StoreAnalyzeResponse>(API_ENDPOINTS.STORE_ANALYZE, request)
            return response.data
        } catch (error) {
            console.error('Get store analyze error:', error)
            throw error
        }
    }

    // 取得評論詳情列表（評論中心）
    async getComments(request: CommentsDetailsRequest): Promise<CommentsDetailsResponse> {
        try {
            const response = await this.api.post<CommentsDetailsResponse>(API_ENDPOINTS.COMMENTS, request)
            return response.data
        } catch (error) {
            console.error('Get comments error:', error)
            throw error
        }
    }

    // 生成評論回覆內容
    async generateReply(request: GenerateReplyRequest): Promise<GenerateReplyResponse> {
        try {
            const response = await this.api.post<GenerateReplyResponse>(API_ENDPOINTS.GENERATE_REPLY, request)
            return response.data
        } catch (error) {
            console.error('Generate reply error:', error)
            throw error
        }
    }

    // 設定店家資料
    async setStoreSettings(request: StoreSettingsRequest): Promise<StoreSettingsResponse> {
        try {
            console.log('========== API Service: 準備發送請求 ==========')
            console.log('API URL:', `${this.baseURL}${API_ENDPOINTS.STORE_SETTINGS}`)
            console.log('請求方法: POST')
            console.log('請求標頭: Content-Type: application/json')
            console.log('請求參數 (按 API 文件順序):')
            console.log(JSON.stringify({
                store_code: request.store_code,
                store_name: request.store_name,
                comment_url: request.comment_url,
                store_addr: request.store_addr
            }, null, 2))
            console.log('=============================================')

            const response = await this.api.post<StoreSettingsResponse>(API_ENDPOINTS.STORE_SETTINGS, request)

            console.log('========== API Service: 收到響應 ==========')
            console.log('響應狀態碼:', response.status)
            console.log('響應數據:', response.data)
            console.log('==========================================')

            return response.data
        } catch (error) {
            console.error('========== API Service: 請求失敗 ==========')
            console.error('Set store settings error:', error)
            console.error('=========================================')
            throw error
        }
    }

    // 查詢分類或關鍵字
    async searchCategoryKeyword(request: CategoryKeywordRequest): Promise<CategoryKeywordResponse> {
        try {
            const response = await this.api.get<CategoryKeywordResponse>(API_ENDPOINTS.CATEGORY_KEYWORD, {
                params: request
            })
            return response.data
        } catch (error) {
            console.error('Search category keyword error:', error)
            throw error
        }
    }

    // 檢查 store flag - 檢查後端資料處理是否完成
    async checkStoreFlag(request: StoreFlagRequest): Promise<StoreFlagResponse> {
        try {
            const response = await this.api.post<StoreFlagResponse>(API_ENDPOINTS.STORE_FLAG, request)
            return response.data
        } catch (error) {
            console.error('Check store flag error:', error)
            throw error
        }
    }

    // 查詢特定分類或關鍵字的評論
    async queryType(request: QueryTypeRequest): Promise<QueryTypeResponse> {
        try {
            const response = await this.api.post<QueryTypeResponse>(API_ENDPOINTS.QUERY_TYPE, request)
            return response.data
        } catch (error) {
            console.error('Query type error:', error)
            throw error
        }
    }

    // 生成評論回覆
    async replyComments(request: ReplyCommentsRequest): Promise<ReplyCommentsResponse> {
        try {
            const response = await this.api.post<ReplyCommentsResponse>(API_ENDPOINTS.REPLY_COMMENTS, request)
            return response.data
        } catch (error) {
            console.error('Reply comments error:', error)
            throw error
        }
    }

    // 下載評論列表 CSV 檔案
    async downloadComments(storeCode: string): Promise<void> {
        const token = localStorage.getItem('argameusertoken')
        if (!token) {
            throw new Error('未登入，請重新登入')
        }

        const url = `${this.baseURL}${API_ENDPOINTS.COMMENTS_DOWNLOAD}?store_code=${encodeURIComponent(storeCode)}`
        console.log('========== 下載評論 CSV ==========')
        console.log('請求 URL:', url)

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })

        if (!response.ok) {
            console.error('下載失敗，HTTP 狀態碼:', response.status)
            throw new Error(`下載失敗（HTTP ${response.status}）`)
        }

        // 取得 blob 資料
        const blob = await response.blob()

        // 產生帶時間戳的檔案名稱 comments_YYYYMMDD_HHMMSS.csv
        const now = new Date()
        const pad = (n: number) => String(n).padStart(2, '0')
        const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
        const filename = `comments_${timestamp}.csv`

        // 建立下載連結並自動點擊
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)

        console.log('✅ 評論 CSV 已下載:', filename)
        console.log('==================================')
    }

    // 登出
    logout(): void {
        // 清除認證相關資料
        localStorage.removeItem('argameusertoken')
        localStorage.removeItem('store_code')

        // 清除其他登入資料
        localStorage.removeItem('login_message')
        localStorage.removeItem('login_status')
        localStorage.removeItem('isCompleteData')
        localStorage.removeItem('login_timestamp')
        localStorage.removeItem('login_data')

        // 清除店家資訊
        this.clearStoreInfo()

        console.log('已清除所有登入資料')
    }

    // 檢查是否已登入
    isLoggedIn(): boolean {
        return !!localStorage.getItem('argameusertoken')
    }

    // 取得當前 store_code
    getCurrentStoreCode(): string | null {
        return localStorage.getItem('store_code')
    }

    // 取得當前 token
    getCurrentToken(): string | null {
        return localStorage.getItem('argameusertoken')
    }

    // 取得登入訊息
    getLoginMessage(): string | null {
        return localStorage.getItem('login_message')
    }

    // 取得登入狀態
    getLoginStatus(): string | null {
        return localStorage.getItem('login_status')
    }

    // 取得資料完整性狀態
    getIsCompleteData(): string | null {
        return localStorage.getItem('isCompleteData')
    }

    // 取得登入時間戳
    getLoginTimestamp(): string | null {
        return localStorage.getItem('login_timestamp')
    }

    // 取得完整的登入資料
    getLoginData(): any | null {
        const loginDataStr = localStorage.getItem('login_data')
        if (loginDataStr) {
            try {
                return JSON.parse(loginDataStr)
            } catch (error) {
                console.error('解析登入資料失敗:', error)
                return null
            }
        }
        return null
    }

    // 檢查資料是否完整
    isDataComplete(): boolean {
        return this.getIsCompleteData() === 'true'
    }

    // 儲存店家資訊
    saveStoreInfo(storeInfo: StoreInfo): void {
        localStorage.setItem('store_info', JSON.stringify(storeInfo))
        console.log('店家資訊已儲存:', storeInfo)
    }

    // 取得店家資訊
    getStoreInfo(): StoreInfo | null {
        const storeInfoStr = localStorage.getItem('store_info')
        if (storeInfoStr) {
            try {
                return JSON.parse(storeInfoStr)
            } catch (error) {
                console.error('解析店家資訊失敗:', error)
                return null
            }
        }
        return null
    }

    // 清除店家資訊
    clearStoreInfo(): void {
        localStorage.removeItem('store_info')
        console.log('店家資訊已清除')
    }
}

// 建立單例實例
const apiService = new ApiService()

export default apiService
export { ApiService }
